import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Package, Recycle, Inbox, FileText, Receipt, LogOut, FlaskConical, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/refurbished", icon: Recycle, label: "Refurbished" },
  { to: "/admin/enquiries", icon: Inbox, label: "Enquiries" },
  { to: "/admin/quotations", icon: FileText, label: "Quotations" },
  { to: "/admin/invoices", icon: Receipt, label: "Invoices" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <div className="flex min-h-screen items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex min-h-screen bg-slate-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <aside className="hidden w-60 flex-col bg-[#0F2942] text-slate-200 lg:flex">
        <div className="flex items-center gap-2 px-6 py-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10">
            <FlaskConical className="h-5 w-5 text-teal-300" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>HiCare Admin</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-300">Console</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to} to={to} end={end}
              data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 px-3 py-4">
          <div className="mb-2 px-3 text-xs text-slate-400 truncate">{user.email}</div>
          <Button data-testid="admin-logout" onClick={async () => { await logout(); nav("/admin/login"); }} variant="ghost" className="w-full justify-start text-slate-200 hover:bg-white/10 hover:text-white">
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="w-full lg:hidden">
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0F2942]">
              <FlaskConical className="h-4 w-4 text-teal-300" />
            </div>
            <div className="text-sm font-bold text-[#0F2942]">HiCare Admin</div>
          </div>
          <Button onClick={async () => { await logout(); nav("/admin/login"); }} variant="ghost" size="sm"><LogOut className="h-4 w-4" /></Button>
        </div>
        <div className="flex overflow-x-auto border-b border-slate-200 bg-white">
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap px-4 py-2 text-xs font-medium ${
                  isActive ? "border-b-2 border-[#0F2942] text-[#0F2942]" : "text-slate-500"
                }`
              }>
              <Icon className="h-3.5 w-3.5" /> {label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 overflow-auto p-6 lg:p-10"><Outlet /></main>
    </div>
  );
}
